import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import CircularProgress from "../components/CircularProgress";
import SpeechPlayer from "../components/SpeechPlayer";
import { useSpeech, getSpeechPrefs, saveSpeechPrefs } from "../hooks/useSpeech";
import { ArrowLeft, Edit3, Save, Download, Trash2, FileText, Pencil, X, Loader2, Sparkles, FileBarChart } from "lucide-react";
import { toast } from "sonner";

const READING_STYLE = {
  backgroundColor: "rgba(99,102,241,0.28)",
  borderRadius: "3px",
  boxShadow: "0 0 0 2px rgba(99,102,241,0.22)",
  paddingInline: "2px",
  transition: "background-color 0.15s ease",
};

export default function DocumentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [doc, setDoc]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName]   = useState("");
  const [editing, setEditing]   = useState(false);
  const [editText, setEditText] = useState("");
  const [busy, setBusy]         = useState(false);

  // Speech
  const { supported, browserSupported, voices, speaking, paused, currentSentIdx, progress, speak, stop, pause, resume } = useSpeech();
  const prefs0 = getSpeechPrefs();
  const [speechRate, setSpeechRate]   = useState(prefs0.rate    || 1);
  const [speechVoice, setSpeechVoice] = useState(prefs0.voiceName || "");
  const [engineMode, setEngineMode]   = useState(prefs0.engineMode || "browser");
  const sentRefs = useRef({});

  useEffect(() => {
    if (currentSentIdx >= 0 && sentRefs.current[currentSentIdx]) {
      sentRefs.current[currentSentIdx].scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [currentSentIdx]);

  useEffect(() => () => stop(), []); // eslint-disable-line

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/documents/${id}/full`);
      setDoc(data);
      setNewName(data.fileName);
      setEditText((data.currentSentences || []).join(" "));
    } catch {
      toast.error("Document not found");
      nav("/history");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const saveRename = async () => {
    if (!newName.trim()) return;
    try {
      await api.put(`/documents/${id}/rename`, { fileName: newName.trim() });
      toast.success("Renamed"); setRenaming(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Rename failed"); }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/documents/${id}/edit`, { text: editText });
      setDoc(data); toast.success("Saved & re-scanned"); setEditing(false);
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const paraphrase = async () => {
    setBusy(true); stop();
    try {
      const { data } = await api.post(`/documents/${id}/paraphrase`);
      setDoc(data);
      setEditText((data.currentSentences || []).join(" "));
      toast.success(`Round ${data.rounds.length} done`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Paraphrase failed"); }
    finally { setBusy(false); }
  };

  const downloadAs = async (kind) => {
    const path = kind === "report" ? `/documents/${id}/report` : `/documents/${id}/download?version=${kind}`;
    const { data } = await api.get(path);
    let blob;
    if (data.encoding === "base64") {
      const bytes = atob(data.content);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      blob = new Blob([arr], { type: data.mimeType });
    } else {
      blob = new Blob([data.content], { type: "text/plain" });
    }
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = data.fileName; a.click();
    URL.revokeObjectURL(a.href);
  };

  const deleteDoc = async () => {
    if (!window.confirm("Delete this document permanently?")) return;
    await api.delete(`/documents/${id}`);
    toast.success("Deleted"); nav("/history");
  };

  const deleteReport = async () => {
    if (!window.confirm("Clear scan report and rounds?")) return;
    await api.delete(`/documents/${id}/report`);
    toast.success("Report cleared"); load();
  };

  if (loading || !doc) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-400">Loading…</div>;
  }

  const lastScan = doc.lastScan || {};
  const scores   = lastScan.sentence_scores || [];
  const aiPct    = doc.finalAiPercentage ?? 0;
  const plagPct  = doc.finalPlagiarismPercentage ?? 0;
  const flagged  = scores.filter(s => s.isAI).length;
  const rounds   = doc.rounds || [];

  // Speech handlers
  const handlePlay = () => {
    if (paused) { resume(); return; }
    const sents = scores.length ? scores.map(s => s.text) : (doc.currentSentences || []);
    speak(sents, speechRate, speechVoice);
  };
  const handleRateChange  = (r) => { setSpeechRate(r);  saveSpeechPrefs({ rate: r }); };
  const handleVoiceChange = (v) => { setSpeechVoice(v); saveSpeechPrefs({ voiceName: v }); };
  const handleEngineMode  = (m) => { setEngineMode(m);  saveSpeechPrefs({ engineMode: m }); };

  const showPlayer = (scores.length > 0 || (doc.currentSentences || []).length > 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-12">
      <Link to="/history" className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-zinc-400 hover:text-white mb-4">
        <ArrowLeft size={14}/> Back to history
      </Link>

      {/* Header */}
      <div className="glass-strong rounded-2xl sm:rounded-3xl p-5 sm:p-7">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-12 h-12 rounded-xl bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center flex-shrink-0">
            <FileText size={20} className="text-indigo-300"/>
          </div>
          <div className="flex-1 min-w-0">
            {renaming ? (
              <div className="flex items-center gap-2">
                <Input data-testid="rename-input" value={newName} onChange={e=>setNewName(e.target.value)} className="h-9 max-w-md"/>
                <Button size="sm" onClick={saveRename} className="bg-indigo-600 hover:bg-indigo-700" data-testid="rename-save-btn"><Save size={14}/></Button>
                <Button size="sm" variant="ghost" onClick={()=>{setRenaming(false); setNewName(doc.fileName);}}><X size={14}/></Button>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-bold tracking-tight truncate" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>{doc.fileName}</h1>
                <button onClick={()=>setRenaming(true)} data-testid="rename-btn" className="p-1.5 rounded hover:bg-white/10 text-zinc-400" title="Rename">
                  <Pencil size={14}/>
                </button>
              </div>
            )}
            <div className="mt-1 flex items-center gap-3 flex-wrap text-xs text-zinc-400">
              <span>{doc.fileType?.toUpperCase()}</span>
              <span>·</span><span>{doc.originalWordCount?.toLocaleString()} words</span>
              <span>·</span><span>{(doc.uploadedAt || "").split("T")[0]}</span>
              <span>·</span><span className="px-1.5 py-0.5 rounded bg-white/[0.06] uppercase">{doc.status}</span>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
          <div className="flex items-center justify-center sm:justify-start">
            <CircularProgress value={aiPct} color={aiPct > 0 ? "#EF4444" : "#10B981"} label="AI Detected" sublabel={`${Math.max(0,100-aiPct)}% Human`} size={130} stroke={10}/>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-1 gap-2">
            <div className="glass rounded-lg p-3"><div className="text-[10px] uppercase tracking-widest text-zinc-400">Plagiarism</div><div className="text-xl font-bold mt-0.5">{plagPct}%</div></div>
            <div className="glass rounded-lg p-3"><div className="text-[10px] uppercase tracking-widest text-zinc-400">Flagged</div><div className="text-xl font-bold mt-0.5">{flagged}</div></div>
          </div>
          <div className="glass rounded-lg p-3 col-span-1 sm:col-span-2 lg:col-span-1">
            <div className="text-[10px] uppercase tracking-widest text-zinc-400 mb-2">Rounds ({rounds.length})</div>
            <div className="flex flex-wrap gap-1.5 max-h-20 overflow-y-auto">
              {rounds.length === 0 && <span className="text-xs text-zinc-500">Initial scan only</span>}
              {rounds.map(r => (
                <span key={r.roundNumber} className="px-1.5 py-0.5 rounded bg-white/[0.04] text-[10px] font-mono">R{r.roundNumber}: {r.aiPercentageBefore}→{r.aiPercentageAfter}%</span>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {aiPct > 0 ? (
              <Button onClick={paraphrase} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 glow-primary" data-testid="detail-paraphrase-btn">
                {busy ? <Loader2 size={14} className="animate-spin mr-2"/> : <Sparkles size={14} className="mr-2"/>}
                Paraphrase R{rounds.length+1}
              </Button>
            ) : (
              <div className="rounded-lg p-2.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs flex items-center gap-2">
                <Sparkles size={14}/> 100% Human ✨
              </div>
            )}
            <Button onClick={deleteDoc} variant="outline" className="border-red-500/30 text-red-300 hover:bg-red-500/10 hover:text-red-200" data-testid="detail-delete-btn">
              <Trash2 size={14} className="mr-2"/>Delete document
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="paraphrased" className="mt-6">
        <TabsList className="glass-strong">
          <TabsTrigger value="paraphrased" data-testid="tab-paraphrased">Paraphrased</TabsTrigger>
          <TabsTrigger value="original" data-testid="tab-original">Original</TabsTrigger>
          <TabsTrigger value="report" data-testid="tab-report">Report</TabsTrigger>
        </TabsList>

        {/* Paraphrased tab */}
        <TabsContent value="paraphrased">
          <div className="glass-strong rounded-2xl p-5 sm:p-7">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h3 className="font-semibold">Humanized version</h3>
              <div className="flex gap-2">
                {!editing ? (
                  <>
                    <Button size="sm" variant="outline" onClick={()=>setEditing(true)} data-testid="edit-text-btn"><Edit3 size={14} className="mr-2"/>Edit</Button>
                    <Button size="sm" onClick={()=>downloadAs("paraphrased")} className="bg-indigo-600 hover:bg-indigo-700" data-testid="download-paraphrased-btn"><Download size={14} className="mr-2"/>Download</Button>
                  </>
                ) : (
                  <>
                    <Button size="sm" variant="ghost" onClick={()=>{setEditing(false); setEditText((doc.currentSentences||[]).join(" "));}}><X size={14} className="mr-2"/>Cancel</Button>
                    <Button size="sm" onClick={saveEdit} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-edit-btn">
                      {busy ? <Loader2 size={14} className="animate-spin mr-2"/> : <Save size={14} className="mr-2"/>}Save & re-scan
                    </Button>
                  </>
                )}
              </div>
            </div>

            {editing ? (
              <Textarea data-testid="edit-textarea" value={editText} onChange={e=>setEditText(e.target.value)}
                className="min-h-[300px] sm:min-h-[450px] font-mono text-sm leading-relaxed bg-white/[0.02] border-white/[0.08]"/>
            ) : (
              <>
                {/* Sentence-highlighted text */}
                <div className="prose prose-invert max-w-none text-sm leading-7 max-h-[55vh] overflow-y-auto pr-2 whitespace-pre-wrap scroll-slim">
                  {scores.length === 0 ? (
                    <div className="text-zinc-500">{(doc.currentSentences||[]).join(" ")}</div>
                  ) : (
                    scores.map(s => (
                      <span
                        key={s.idx}
                        ref={el => { sentRefs.current[s.idx] = el; }}
                        className={s.isAI ? "sentence-ai" : "sentence-human"}
                        style={currentSentIdx === s.idx ? READING_STYLE : {}}
                        title={`AI score: ${Math.round(s.score)}%`}
                      >
                        {s.text}{" "}
                      </span>
                    ))
                  )}
                </div>

                {/* Speech player */}
                {showPlayer && (
                  <SpeechPlayer
                    supported={supported}
                    browserSupported={browserSupported}
                    speaking={speaking}
                    paused={paused}
                    progress={progress}
                    voices={voices}
                    rate={speechRate}
                    voiceName={speechVoice}
                    engineMode={engineMode}
                    onPlay={handlePlay}
                    onPause={pause}
                    onStop={stop}
                    onRateChange={handleRateChange}
                    onVoiceChange={handleVoiceChange}
                    onEngineModeChange={handleEngineMode}
                  />
                )}
              </>
            )}
          </div>
        </TabsContent>

        {/* Original tab */}
        <TabsContent value="original">
          <div className="glass-strong rounded-2xl p-5 sm:p-7">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Original (as uploaded)</h3>
              <Button size="sm" variant="outline" onClick={()=>downloadAs("original")} data-testid="download-original-btn"><Download size={14} className="mr-2"/>Download original</Button>
            </div>
            <div className="text-sm leading-7 max-h-[60vh] overflow-y-auto pr-2 whitespace-pre-wrap text-zinc-300 scroll-slim">
              {doc.originalText || (doc.originalSentences || []).join(" ")}
            </div>
          </div>
        </TabsContent>

        {/* Report tab */}
        <TabsContent value="report">
          <div className="glass-strong rounded-2xl p-5 sm:p-7">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h3 className="font-semibold flex items-center gap-2"><FileBarChart size={16} className="text-cyan-300"/>Detection report</h3>
              <div className="flex gap-2">
                <Button size="sm" onClick={()=>downloadAs("report")} className="bg-indigo-600 hover:bg-indigo-700" data-testid="download-report-btn"><Download size={14} className="mr-2"/>Download report</Button>
                <Button size="sm" variant="outline" onClick={deleteReport} className="border-red-500/30 text-red-300 hover:bg-red-500/10" data-testid="delete-report-btn"><Trash2 size={14} className="mr-2"/>Clear report</Button>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3 mb-5">
              <div className="glass rounded-lg p-3"><div className="text-[10px] uppercase tracking-widest text-zinc-400">AI %</div><div className="text-2xl font-bold">{aiPct}%</div></div>
              <div className="glass rounded-lg p-3"><div className="text-[10px] uppercase tracking-widest text-zinc-400">Plagiarism</div><div className="text-2xl font-bold">{plagPct}%</div></div>
              <div className="glass rounded-lg p-3"><div className="text-[10px] uppercase tracking-widest text-zinc-400">Sentences flagged</div><div className="text-2xl font-bold">{flagged}/{scores.length}</div></div>
            </div>
            <div className="text-xs uppercase tracking-widest text-zinc-400 mb-2">Round progression</div>
            {rounds.length === 0 ? (
              <div className="text-sm text-zinc-500 mb-5">No paraphrase rounds yet.</div>
            ) : (
              <div className="space-y-1.5 mb-5">
                {rounds.map(r => (
                  <div key={r.roundNumber} className="glass rounded-lg p-2.5 flex items-center justify-between text-xs sm:text-sm">
                    <span className="font-mono text-zinc-300">Round {r.roundNumber}</span>
                    <span className="text-zinc-400">{r.flaggedSentences} flagged</span>
                    <span><span className="text-red-300">{r.aiPercentageBefore}%</span> → <span className="text-emerald-300">{r.aiPercentageAfter}%</span></span>
                    <span className="text-zinc-500 hidden sm:inline">{(r.processedAt||"").slice(11,16)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="text-xs uppercase tracking-widest text-zinc-400 mb-2">Sentence-by-sentence</div>
            <div className="max-h-[40vh] overflow-y-auto space-y-1 pr-2 scroll-slim">
              {scores.length === 0 && <div className="text-sm text-zinc-500">No scan data.</div>}
              {scores.map(s => (
                <div key={s.idx} className={`text-xs sm:text-sm rounded-md px-3 py-2 border ${s.isAI ? "bg-red-500/5 border-red-500/20" : "bg-emerald-500/5 border-emerald-500/20"}`}>
                  <div className="flex items-center justify-between gap-3 text-[10px] uppercase tracking-widest mb-1">
                    <span className={s.isAI ? "text-red-300" : "text-emerald-300"}>{s.isAI ? "AI" : "Human"}</span>
                    <span className="font-mono text-zinc-500">{Math.round(s.score)}% AI score</span>
                  </div>
                  <div className="text-zinc-300">{s.text}</div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
